import React, { useState } from "react";
import { brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Receipt } from "lucide-react";

const addDays = (iso, days) => {
  const d = iso ? new Date(`${iso}T12:00:00`) : new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export const defaultInstallments = (nfe) => {
  const fromNote = (nfe.installments || []).filter((i) => i.due_date && i.amount > 0);
  if (fromNote.length) return fromNote.map((i) => ({ due_date: i.due_date, amount: i.amount }));
  return [{ due_date: addDays(null, 30), amount: nfe.total }];
};

export const splitEqually = (total, n, firstDate) => {
  const per = Math.floor((total / n) * 100) / 100;
  return Array.from({ length: n }, (_, i) => ({
    due_date: addDays(firstDate, 30 * i),
    amount: i === n - 1 ? +(total - per * (n - 1)).toFixed(2) : per,
  }));
};

export const NfePayablesStep = ({ nfe, rows, setRows }) => {
  const sum = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const diff = +(nfe.total - sum).toFixed(2);
  const upd = (idx, k, v) => setRows(rows.map((r, j) => (j === idx ? { ...r, [k]: v } : r)));
  const fromNote = (nfe.installments || []).length > 0;
  const [splitN, setSplitN] = useState(rows.length || 1);
  return (
    <div className="space-y-4" data-testid="nfe-payables-step">
      <div className="flex items-start gap-3 border rounded-lg p-3 bg-slate-50 text-sm">
        <Receipt className="w-5 h-5 text-blue-600 mt-0.5" />
        <div>
          <div className="font-semibold">Contas a pagar desta entrada</div>
          <div className="text-xs text-slate-500">{fromNote ? "Parcelas lidas da nota fiscal." : "A nota não tem parcelas; sugerimos 1 parcela com o total."} Ajuste datas, valores e número de parcelas.</div>
        </div>
      </div>
      <div className="space-y-2">
        {rows.map((r, idx) => (
          <div key={`p-${idx}`} className="grid grid-cols-[auto_1fr_1fr_auto] gap-2 items-center" data-testid={`payable-row-${idx}`}>
            <span className="text-xs text-slate-500 w-10">{idx + 1}/{rows.length}</span>
            <Input type="date" value={r.due_date} onChange={(e) => upd(idx, "due_date", e.target.value)} data-testid={`payable-due-${idx}`} />
            <Input type="number" step="0.01" value={r.amount} onChange={(e) => upd(idx, "amount", e.target.value)} className="text-right" data-testid={`payable-amount-${idx}`} />
            <Button type="button" size="icon" variant="ghost" className="text-rose-600" disabled={rows.length === 1}
              onClick={() => setRows(rows.filter((_, j) => j !== idx))} data-testid={`payable-del-${idx}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 items-end">
        <Button type="button" variant="outline" size="sm" onClick={() => setRows([...rows, { due_date: addDays(rows[rows.length - 1]?.due_date, 30), amount: diff > 0 ? diff : 0 }])} data-testid="payable-add-btn">
          <Plus className="w-3 h-3 mr-1" /> Parcela
        </Button>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs">Dividir em</Label>
            <Input type="number" min="1" className="w-20 h-9" value={splitN} onChange={(e) => setSplitN(e.target.value)} data-testid="payable-split-n" /></div>
          <Button type="button" variant="outline" size="sm" data-testid="payable-split-btn"
            onClick={() => setRows(splitEqually(nfe.total, Math.max(1, Number(splitN) || 1), rows[0]?.due_date))}>
            Parcelas iguais
          </Button>
        </div>
      </div>
      <div className="text-sm flex justify-between border-t pt-2">
        <span>Total das parcelas: <b className="kpi-num" data-testid="payable-sum">{brl(sum)}</b> • Nota: <span className="kpi-num">{brl(nfe.total)}</span></span>
        {Math.abs(diff) >= 0.01 && <span className="text-amber-700 text-xs" data-testid="payable-diff">Diferença de {brl(diff)}</span>}
      </div>
    </div>
  );
};
